import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  FaHome, FaBuilding, FaHandshake, FaCalculator,
  FaTasks, FaComments, FaFolderOpen, FaFileAlt,
  FaChartBar, FaPuzzlePiece, FaBolt, FaPlug,
  FaQuestionCircle, FaCog, FaUserCircle, FaChevronLeft,
  FaWrench, FaChevronDown, FaChevronRight
} from 'react-icons/fa';

const NAV_ITEMS = [
  { to: '/pm',             label: 'Dashboard',       Icon: FaHome },
  { to: '/pm/properties',  label: 'Rentals',         Icon: FaBuilding },
  {
    to: '/pm/leasing',
    label: 'Leasing',
    Icon: FaHandshake,
    hasSub: true,
    sub: [
      { to: '/pm/leasing/listings',       label: 'Listings' },
      { to: '/pm/leasing/prospects',      label: 'Prospects' },
      { to: '/pm/leasing/applicants',     label: 'Applicants' },
      { to: '/pm/leasing/draft-leases',   label: 'Draft Leases' },
      { to: '/pm/leasing/renewals',       label: 'Lease Renewals' },
      { to: '/pm/leasing',                label: 'Leasing' }
    ]
  },
  {
    to: '/pm/accounting',
    label: 'Accounting',
    Icon: FaCalculator,
    hasSub: true,
    sub: [
      { to: '/pm/accounting/financials', label: 'Financials' },
      { to: '/pm/accounting/banking', label: 'Banking' },
      { to: '/pm/accounting/bills', label: 'Bills' },
      { to: '/pm/accounting/recurring', label: 'Recurring transactions' },
      { to: '/pm/accounting/eft', label: 'EFT approvals' },
      { to: '/pm/accounting/budgets', label: 'Budgets' },
      { to: '/pm/accounting/chart-of-accounts', label: 'Chart of accounts' },
      { to: '/pm/accounting/company', label: 'Company financials' },
      { to: '/pm/accounting/1099', label: '1099 tax filings' }
    ]
  },
  {
    to: '/pm/maintenance',
    label: 'Maintenance',
    Icon: FaWrench,
    hasSub: true,
    sub: [
      { to: '/pm/maintenance/vendors',             label: 'Vendors' },
      { to: '/pm/maintenance/work-orders',         label: 'Work Orders' },
      { to: '/pm/maintenance/inspections',         label: 'Property Inspections' },
    ]
  },
  { to: '/pm/tasks',       label: 'Tasks',           Icon: FaTasks },
  { to: '/pm/communication', label: 'Communication', Icon: FaComments },
  { to: '/pm/files',       label: 'Files',           Icon: FaFolderOpen },
  { to: '/pm/reports',     label: 'Reports',         Icon: FaFileAlt },
  { to: '/pm/analytics',   label: 'Analytics Hub',   Icon: FaChartBar },
  { to: '/pm/add-ons',     label: 'Add-on Services', Icon: FaPuzzlePiece },
  { to: '/pm/shortcuts',   label: 'Shortcuts',       Icon: FaBolt },
];

const FOOTER_ITEMS = [
  { to: '/pm/integrations',  label: 'Integrations',   Icon: FaPlug },
  { to: '/pm/help-support',  label: 'Help & Support', Icon: FaQuestionCircle },
  { to: '/pm/settings',      label: 'Settings',       Icon: FaCog },
];

export default function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const [openMaintenance, setOpenMaintenance] = useState(false);
  const [openLeasing, setOpenLeasing] = useState(false);
  const [openAccounting, setOpenAccounting] = useState(false);

  function renderLink(item, isFooter = false) {
    const { to, label, Icon, hasSub, sub } = item;
    const isActiveGroup = window.location.pathname.startsWith(to);

    const isOpen =
      label === 'Maintenance' ? openMaintenance :
      label === 'Leasing' ? openLeasing :
      label === 'Accounting' ? openAccounting : false;

    return (
      <React.Fragment key={to}>
        <NavLink
          to={to}
          end={!hasSub}
          style={({ isActive }) => ({
            ...styles.item,
            ...(isFooter ? styles.footerItem : {}),
            ...(isActive || (hasSub && isActiveGroup) ? styles.active : {}),
            justifyContent: collapsed ? 'center' : 'flex-start'
          })}
          onClick={hasSub ? e => {
            e.preventDefault();
            if (label === 'Maintenance') setOpenMaintenance(!openMaintenance);
            if (label === 'Leasing') setOpenLeasing(!openLeasing);
            if (label === 'Accounting') setOpenAccounting(!openAccounting);
          } : undefined}
        >
          <Icon style={{
            ...styles.icon,
            marginRight: collapsed ? 0 : 12
          }}/>
          {!collapsed && <span style={styles.label}>{label}</span>}
          {hasSub && !collapsed && (
            isOpen
              ? <FaChevronDown style={{ marginLeft: 'auto' }}/>
              : <FaChevronRight style={{ marginLeft: 'auto' }}/>
          )}
        </NavLink>

        {hasSub && isOpen && !collapsed && (
          <div style={styles.subMenu}>
            {sub.map(link => (
              <NavLink
                key={link.to}
                to={link.to}
                style={({ isActive }) => ({
                  ...styles.subItem,
                  ...(isActive ? styles.activeSubItem : {})
                })}
              >
                {link.label}
              </NavLink>
            ))}
          </div>
        )}
      </React.Fragment>
    );
  }

  return (
    <aside style={{
      ...styles.sidebar,
      width: collapsed ? styles.collapsedWidth : styles.expandedWidth
    }}>
      <div>
        <div style={styles.logoArea}>
          <img
            src="https://i.ibb.co/s2dBvDt/elevate.png"
            alt="Elevate"
            style={{
              ...styles.logoImg,
              width: collapsed ? 40 : 300
            }}
          />
          <button
            onClick={() => setCollapsed(!collapsed)}
            style={{
              ...styles.collapseBtn,
              transform: collapsed ? 'rotate(180deg)' : 'none'
            }}
          >
            <FaChevronLeft />
          </button>
        </div>

        {NAV_ITEMS.map(item => renderLink(item))}
      </div>

      <div style={styles.footer}>
        {FOOTER_ITEMS.map(item => renderLink(item, true))}
        {renderLink({ to: '/pm/account', label: 'JR', Icon: FaUserCircle }, true)}
      </div>
    </aside>
  );
}

const styles = {
  expandedWidth: 240,
  collapsedWidth: 64,
  sidebar: {
    display: 'flex', flexDirection: 'column',
    justifyContent: 'space-between', height: '100vh',
    backgroundColor: '#00273D', color: '#fff', transition: 'width 0.2s'
  },
  logoArea: {
    position: 'relative', display: 'flex', alignItems: 'center', padding: 16
  },
  logoImg: { height: 'auto', transition: 'width 0.2s' },
  collapseBtn: {
    position: 'absolute', top: 16, right: -12,
    backgroundColor: '#00273D', border: 'none',
    color: '#fff', cursor: 'pointer', padding: 4, borderRadius: '50%'
  },
  item: {
    display: 'flex', alignItems: 'center',
    padding: '12px 16px', color: '#fff',
    textDecoration: 'none', fontSize: '0.95rem',
    transition: 'background-color 0.2s',
    borderRadius: 4, margin: '4px 8px'
  },
  footerItem: { fontSize: '0.85rem' },
  icon: { fontSize: '1.4rem' },
  label: { whiteSpace: 'nowrap' },
  active: { backgroundColor: '#01457E' },
  footer: {
    borderTop: '1px solid rgba(255,255,255,0.1)',
    padding: '16px 0'
  },
  subMenu: {
    display: 'flex', flexDirection: 'column',
    paddingLeft: 32, marginBottom: 8
  },
  subItem: {
    padding: '8px 0 8px 0',
    color: '#bbb',
    textDecoration: 'none',
    paddingLeft: 16
  },
  activeSubItem: {
    color: '#fff',
    fontWeight: 'bold'
  }
};
